import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/founders.css";
import useReveal from "./useReveal";

export default function Founders() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      <section className="sub-hero" id="top">
        <span className="eye">For founders &amp; CTOs</span>
        <h1>Hire like you have a <em>10-person</em> talent team<span className="period" /></h1>
        <p className="lede-xl">
          You don't have a talent team. You have a quarter to fill a role that, if you get it wrong, costs you the quarter. <b>Ann runs the whole search</b> — so you walk in already knowing, with the conviction of a full TA function and none of the headcount.
        </p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/how-it-works" className="btn btn-secondary btn-lg">How Ann works</Link>
        </div>
      </section>

      {/* THE STAKES */}
      <section className="sub-section">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">The stakes</span>
            <h2 className="sub-h2">At your stage, one hire <em>is</em> the strategy.</h2>
          </div>
          <div className="prose">
            <p className="big">A 50-person company hires a bad VP and absorbs it. A 12-person company hires a bad VP and loses two quarters and three good people.</p>
            <p>So you over-index on the hires that matter — in the cracks between building and raising. You read résumés at 11pm; you take the gut-feel call because there's no time for a tenth conversation. <b>Your most consequential decisions get the least of your attention.</b></p>
            <p>Ann gives those decisions the depth they deserve without the time you don't have — the reading, the sourcing, the first screens — and hands you the few people worth your evening.</p>
          </div>
        </div>
      </section>

      {/* WHAT ANN DOES FOR YOU */}
      <section className="sub-section bone">
        <div className="sub-head">
          <span className="eye">What you get</span>
          <h2 className="sub-h2">The whole search, <em>run for you</em>.</h2>
          <p className="lede">Hand Ann the role — even just a paragraph of what you need. It takes it from there.</p>
        </div>
        <div className="cols-3">
          <div className="point">
            <div className="num">01 · Off your plate</div>
            <h3>The reading, <em>done</em>.</h3>
            <p>Sourcing, résumé-reading, and first screens run without you. The funnel stops being a thing you do at midnight.</p>
          </div>
          <div className="point">
            <div className="num">02 · The five worth meeting</div>
            <h3>A real <em>shortlist</em>.</h3>
            <p>Not fifty maybes — the few candidates worth your time, each with the case for why. You spend your hours on people, not pipelines.</p>
          </div>
          <div className="point">
            <div className="num">03 · Walk in knowing</div>
            <h3>Conviction, <em>backed</em>.</h3>
            <p>A confidence report per candidate — the evidence, the gaps, comparable across the slate. You decide; you just never decide blind.</p>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">What changes</span>
          <h2 className="sub-h2">Speed <em>and</em> conviction — usually a trade-off.</h2>
        </div>
        <div className="metrics">
          <div className="m"><div className="v">9 days</div><div className="k">median brief to<br />decision-ready shortlist</div></div>
          <div className="m"><div className="v">1–5</div><div className="k">critical hires a quarter<br />run end to end</div></div>
          <div className="m"><div className="v">0</div><div className="k">in-house recruiters<br />required to start</div></div>
        </div>
        <p className="prose" style={{ marginTop: 28, fontSize: 13, color: "var(--ink-4)", fontFamily: "var(--f-mono)", letterSpacing: ".04em" }}>// Figures illustrative of early design-partner pilots.</p>
      </section>

      {/* PROOF */}
      <section className="sub-section dark">
        <span className="eye" style={{ display: "block", marginBottom: 32 }}>From a founder</span>
        <p className="pquote">"I hired a VP of Eng in three weeks and I was <span className="cy">sure</span> about it. That's never happened to me before."</p>
        <p className="pattrib"><span className="nm">Co-founder &amp; CEO</span> · seed-stage SaaS · 14 people</p>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">For your next critical hire</span>
        <h2>Bring the role <em>keeping you up</em>.</h2>
        <p className="sub">Fifteen minutes on the hire you're most worried about. See the shortlist Ann would build before you spend another evening on it.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why Ann</Link>
        </div>
      </section>
    </div>
  );
}
